import React, { useState, useEffect, useRef } from "react";
import { Input } from "antd";
import {
  SearchOutlined,
  PhoneOutlined,
  UserOutlined,
  MailOutlined
} from "@ant-design/icons";

const EnhancedLeadSearch = ({ value, onChange, style, isGlobalSearch, placeholder }) => {

  const [display, setDisplay] = useState("");
  const [searchType, setSearchType] = useState("general");
  const inputRef = useRef(null);

  const detectSearchType = (text) => {
    if (!text) return "general";
    if (text.includes("@")) return "email";
    
    // ✅ FIX: Only detect as phone if it's MOSTLY digits (at least 50% digits)
    const digitsOnly = text.replace(/\D/g, "");
    const hasLetters = /[a-zA-Z]/.test(text);
    
    // If it has letters, it's NOT a phone number
    if (hasLetters) return "name";
    
    // Only treat as phone if there are digits and no letters
    if (digitsOnly.length > 0 && !text.includes("@")) return "phone";
    
    return "name";
  };

  const formatUSPhone = (digits) => {
    if (!digits) return "";

    // Only a plain US number (no leading 1, at most 10 digits) is formatted.
    // Anything else is shown as plain digits, so the box never hides a digit
    // that is being searched.
    if (digits.startsWith("1") || digits.length > 10) return digits;

    const d = digits;
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const handleChange = (e) => {
    const raw = e.target.value;

    // ✅ FULL CLEAR
    if (!raw) {
      setDisplay("");
      onChange("");
      setSearchType("general");
      return;
    }

    const type = detectSearchType(raw);
    setSearchType(type);

    // If NOT phone -> normal input with 100 char limit
    if (type !== "phone") {
      const limited = raw.slice(0, 100);
      setDisplay(limited);
      onChange(limited);
      return;
    }

    // PHONE FLOW - Extract only digits
    const digits = raw.replace(/\D/g, "");
    
    // ✅ KEY FIX: If no digits left, clear everything
    if (!digits || digits.length === 0) {
      setDisplay("");
      onChange("");
      setSearchType("general");
      return;
    }

    // A US number with its country code (+19344033466 is how the lead card's
    // Copy button copies a valid number) is searched without the leading 1.
    // That is all the box shows, and it finds the number whether it is stored
    // as (934) 403-3466 or +19344033466.
    const searchDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

    // Format the phone number
    const formatted = formatUSPhone(searchDigits);
    
    setDisplay(formatted);
    onChange(searchDigits); // Send only digits to parent
  };

  useEffect(() => {
    if (!value) {
      setDisplay("");
      return;
    }

    const str = String(value);
    const digits = str.replace(/\D/g, "");

    if (!digits) {
      const limited = str.slice(0, 100);
      setDisplay(limited);
      setSearchType(detectSearchType(limited));
      return;
    }

    // ✅ FIX: Check if it has letters - if yes, don't format as phone
    const hasLetters = /[a-zA-Z]/.test(str);
    if (hasLetters) {
      const limited = str.slice(0, 100);
      setDisplay(limited);
      setSearchType(detectSearchType(limited));
      return;
    }

    // If value has ONLY digits, format as phone
    setDisplay(formatUSPhone(digits));
    setSearchType("phone");
  }, [value]);

  const iconMap = {
    phone: <PhoneOutlined style={{ color: "#1890ff" }} />,
    email: <MailOutlined style={{ color: "#52c41a" }} />,
    name: <UserOutlined style={{ color: "#722ed1" }} />,
    general: <SearchOutlined />
  };

  return (
    <Input
      ref={inputRef}
      value={display}
      onChange={handleChange}
      allowClear
      prefix={iconMap[searchType]}
      size="large"
      placeholder={
        placeholder ||
        (isGlobalSearch
          ? "Search name, email, phone..."
          : "Search leads...")
      }
      style={{
        borderRadius: 8,
        borderColor: isGlobalSearch ? "#1890ff" : undefined,
        ...style
      }}
    />
  );
};

export default EnhancedLeadSearch;